import { useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import useValidateColaborador from "../../hooks/useValidateColaborador";

const SectionTitle = styled.p`
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #94a3b8;
  padding: 0 12px;
  box-sizing: border-box;
  outline: none;
  font-size: 13px;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`;

const ListShell = styled.div`
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }
`;

const ListHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  color: #475569;
  z-index: 1;
`;

const EquipRow = styled.label`
  display: grid;
  grid-template-columns: 24px 70px 1fr 120px 120px;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const EquipCell = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const Counter = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: #263b4b;
`;

export default function ModalOnboardColaborador({
  onClose,
  equipamentos,
  onSaved,
}) {
  const api = useApi();

  const disponiveis = useMemo(
    () => (equipamentos ?? []).filter((e) => e.responsavel === "—"),
    [equipamentos]
  );

  const [matricula, setMatricula] = useState("");
  const [email, setEmail] = useState("");
  const [busca, setBusca] = useState("");
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const {
    info: colabInfo,
    validating,
    error: validationError,
  } = useValidateColaborador(matricula, { bloquearDemitido: true });

  const emailValido = useMemo(() => {
    const e = String(email || "").trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }, [email]);

  const filtrados = useMemo(() => {
    const query = busca.trim().toUpperCase();
    if (!query) return disponiveis;
    return disponiveis.filter((e) => {
      const hay = [e.id, e.tipo, e.descricao, e.patrimonio, e.serial]
        .filter(Boolean)
        .join(" ")
        .toUpperCase();
      return hay.includes(query);
    });
  }, [disponiveis, busca]);

  const todosFiltradosSelecionados =
    filtrados.length > 0 && filtrados.every((e) => selecionados.has(e.id));

  const toggleEquip = (id) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTodos = () => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (todosFiltradosSelecionados) {
        filtrados.forEach((e) => next.delete(e.id));
      } else {
        filtrados.forEach((e) => next.add(e.id));
      }
      return next;
    });
  };

  const canSubmit =
    matricula.trim().length === 6 &&
    selecionados.size > 0 &&
    !saving &&
    !validating &&
    !validationError &&
    Boolean(colabInfo) &&
    emailValido;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);
      const { data } = await api.post("/Painel/API/InventarioTI/OnboardColaborador/", {
        matricula: matricula.trim(),
        email: email.trim(),
        ids_ativo: Array.from(selecionados).map((id) => Number(id)),
      });
      console.log(data);
      onClose?.();
      onSaved?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao realizar onboard.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="lg">
        <Formulario onSubmit={handleSubmit}>
          <FormComp
            title="MATRÍCULA DO COLABORADOR"
            type="numberOnly"
            placeholder="EX: 012345"
            valor={matricula}
            name="matricula"
            changeFunc={(e) => setMatricula(e.target.value.replace(/\D/g, ""))}
            required
            maxLength={6}
          />
          <FormComp
            title="NOME"
            type="text"
            readOnly
            valor={colabInfo?.nome || ""}
            placeholder={validating ? "Validando matrícula..." : ""}
          />
          <FormComp
            title="FUNÇÃO"
            type="text"
            readOnly
            valor={colabInfo?.funcao || ""}
          />
          <FormComp
            title="CENTRO DE CUSTO"
            type="text"
            readOnly
            valor={colabInfo?.centro_custo_desc || colabInfo?.centro_custo || ""}
          />
          <FormComp
            title="E-MAIL DO COLABORADOR"
            type="email"
            placeholder="EX: colaborador@palmont.com.br"
            valor={email}
            name="email"
            changeFunc={(e) => setEmail(e.target.value)}
            required
            invalid={email.length > 0 && !emailValido}
          />
          {validationError && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              {validationError}
            </p>
          )}

          <div>
            <SectionTitle>EQUIPAMENTOS DISPONÍVEIS</SectionTitle>
            <SearchInput
              placeholder="Pesquisar por patrimônio, serial, equipamento..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />

            <ListShell>
              <ListHeader>
                <input
                  type="checkbox"
                  checked={todosFiltradosSelecionados}
                  onChange={toggleTodos}
                  disabled={filtrados.length === 0}
                  aria-label="Selecionar todos"
                />
                <span>Selecionar todos</span>
                <Counter>
                  {selecionados.size} selecionado
                  {selecionados.size === 1 ? "" : "s"} •{" "}
                  {disponiveis.length} disponíve
                  {disponiveis.length === 1 ? "l" : "is"}
                </Counter>
              </ListHeader>

              {filtrados.length === 0 ? (
                <EmptyRow>
                  {disponiveis.length === 0
                    ? "Nenhum equipamento disponível no momento."
                    : "Nenhum equipamento encontrado para a busca."}
                </EmptyRow>
              ) : (
                filtrados.map((e) => (
                  <EquipRow key={e.id}>
                    <input
                      type="checkbox"
                      checked={selecionados.has(e.id)}
                      onChange={() => toggleEquip(e.id)}
                    />
                    <EquipCell title={e.id}>{e.id}</EquipCell>
                    <EquipCell title={e.descricao}>
                      {e.tipo !== "—" ? `${e.tipo} — ` : ""}
                      {e.descricao}
                    </EquipCell>
                    <EquipCell title={e.patrimonio}>{e.patrimonio}</EquipCell>
                    <EquipCell title={e.serial}>{e.serial}</EquipCell>
                  </EquipRow>
                ))
              )}
            </ListShell>
          </div>

          <FormComp
            type="submit"
            valor={
              saving
                ? "SALVANDO..."
                : validating
                ? "VALIDANDO..."
                : `ATRIBUIR ${selecionados.size} EQUIPAMENTO${
                    selecionados.size === 1 ? "" : "S"
                  }`
            }
            disabled={!canSubmit}
          />
        </Formulario>
      </Modal>
    </Overlay>
  );
}
